import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const popularSearches = [
  { en: 'Wireless Headphones', he: 'אוזניות אלחוטיות' },
  { en: 'Smart Watch', he: 'שעון חכם' },
  { en: 'Phone Case', he: 'כיסוי לטלפון' },
  { en: 'LED Lights', he: 'נורות לד' },
];

export default function SearchBar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isRTL = i18n.dir() === 'rtl';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsFocused(false);
    }
  };

  const handlePopularClick = (term: { en: string; he: string }) => {
    const searchTerm = isRTL ? term.he : term.en;
    setQuery(searchTerm);
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    setIsFocused(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <div className={`
          flex items-center bg-white rounded-full overflow-hidden
          border-2 transition-all duration-300 shadow-sm
          ${isFocused
            ? 'border-[#0F2A44] shadow-lg shadow-[#0F2A44]/20'
            : 'border-transparent hover:border-gray-200'
          }
        `}>
          {/* Search icon */}
          <div className="ps-5 text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            placeholder={t('common.searchPlaceholder')}
            className="flex-1 px-4 py-3.5 bg-transparent outline-none text-gray-700 placeholder:text-gray-400"
          />

          {/* Clear button */}
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Search button */}
          <button
            type="submit"
            className="px-4 sm:px-6 py-3.5 bg-[#0F2A44] text-white font-semibold hover:bg-[#1a3a5c] transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden sm:inline">{t('common.search', 'Search')}</span>
          </button>
        </div>
      </form>

      {/* Popular searches dropdown */}
      {isFocused && !query && (
        <div className="absolute top-full start-0 end-0 mt-2 bg-white rounded-xl shadow-dropdown border border-gray-100 py-3 z-50 animate-fade-in">
          <div className="px-4 mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {isRTL ? 'חיפושים פופולריים' : 'Popular Searches'}
            </span>
          </div>
          <div className="space-y-1">
            {popularSearches.map((term, index) => (
              <button
                key={index}
                onClick={() => handlePopularClick(term)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-start"
              >
                <span className="text-[#0F2A44]">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <span className="text-gray-700">{isRTL ? term.he : term.en}</span>
                <span className="ms-auto text-xs text-gray-400">🔥</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
